import { useMemo } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useQuoteBuilderApi } from '../api/client'
import { quoteFilesApi, quoteFilesKey, validateFile, type FileUpload, type QuoteFile, type QuoteFilesApi } from './api'
import { getApiErrorMessage } from '@/lib/api-error'

// Ported from the demo's use-quote-files.ts.

export type UploadState = {
  key: number
  source: File
  status: 'uploading' | 'error'
  progress: number
  error?: string
  reservation?: FileUpload
  transferred?: boolean
}
let sequence = 0

// Uploads live alongside the shared list so closing either surface does not lose a partial batch.
export function fileUploads(client: QueryClient, files: QuoteFilesApi, quoteId?: string | null) {
  const queryKey = quoteFilesKey(quoteId)
  const uploadsKey = [...queryKey, 'uploads']
  const current = () => client.getQueryData<UploadState[]>(uploadsKey) || []
  const update = (key: number, patch: Partial<UploadState>) =>
    client.setQueryData<UploadState[]>(uploadsKey, (items = []) =>
      items.map((item) => (item.key === key ? { ...item, ...patch } : item)),
    )
  const dismiss = async (key: number) => {
    const item = current().find((value) => value.key === key)
    if (!item || item.status === 'uploading') return
    try {
      if (item.reservation) await files.remove(item.reservation.file.id)
      client.setQueryData<UploadState[]>(uploadsKey, (items = []) => items.filter((value) => value.key !== key))
      void client.invalidateQueries({ queryKey, exact: true })
    } catch (error) {
      update(key, { error: getApiErrorMessage(error, 'Could not remove this file. Try again.') })
    }
  }
  const run = async (item: UploadState): Promise<QuoteFile | undefined> => {
    update(item.key, { status: 'uploading', error: undefined, progress: item.transferred ? 100 : 0 })
    try {
      const invalid = validateFile(item.source)
      if (invalid) throw new Error(invalid)
      let reservation = item.reservation
      if (!item.transferred) {
        // A retry gets a fresh signed URL. Remove the old incomplete reservation first.
        if (reservation) {
          await files.remove(reservation.file.id)
          update(item.key, { reservation: undefined })
        }
        reservation = await files.initiate(item.source, quoteId || null)
        update(item.key, { reservation })
        await files.put(reservation, item.source, (progress) => update(item.key, { progress }))
        update(item.key, { transferred: true, progress: 100 })
      }
      const file = await files.complete(reservation!.file.id)
      if (!file.ready) throw new Error('This file is not ready yet. Retry to finish processing it.')
      await client.cancelQueries({ queryKey, exact: true })
      client.setQueryData<QuoteFile[]>(queryKey, (list = []) => [...list.filter((value) => value.id !== file.id), file])
      client.setQueryData<UploadState[]>(uploadsKey, (items = []) => items.filter((value) => value.key !== item.key))
      void client.invalidateQueries({ queryKey, exact: true })
      return file
    } catch (error) {
      update(item.key, { status: 'error', error: getApiErrorMessage(error, 'Upload failed. Retry this file.') })
      return undefined
    }
  }
  return {
    add: (sources: File[]) => {
      const items: UploadState[] = sources.map((source) => ({ key: ++sequence, source, status: 'uploading', progress: 0 }))
      client.setQueryData<UploadState[]>(uploadsKey, (previous = []) => [...previous, ...items])
      return Promise.all(items.map(run))
    },
    retry: (key: number) => {
      const item = current().find((value) => value.key === key)
      return item?.status === 'error' ? run(item) : Promise.resolve(undefined)
    },
    dismiss,
  }
}

export function useQuoteFilesApi() {
  const api = useQuoteBuilderApi()
  return useMemo(() => quoteFilesApi(api), [api])
}

/** The Files library of one quote (or the user's unassigned files): list, uploads in flight, and actions. */
export function useQuoteFiles(quoteId?: string | null) {
  const client = useQueryClient()
  const files = useQuoteFilesApi()
  const queryKey = quoteFilesKey(quoteId)
  const list = useQuery({ queryKey, queryFn: ({ signal }) => files.list(quoteId, signal) })
  const uploads = useQuery<UploadState[]>({
    queryKey: [...queryKey, 'uploads'],
    queryFn: () => [],
    initialData: [],
    enabled: false,
    gcTime: Infinity,
  })
  const operations = fileUploads(client, files, quoteId)
  return {
    files: list,
    uploads: uploads.data || [],
    ...operations,
    blocked: !!uploads.data?.length || !!list.data?.some((file) => !file.ready),
  }
}
